import { APP_INITIALIZER, NgModule } from '@angular/core';

import { registerLocaleData } from '@angular/common';
import cs from '@angular/common/locales/cs';
import de from '@angular/common/locales/de';
import fr from '@angular/common/locales/fr';
import vi from '@angular/common/locales/vi';
import en from '@angular/common/locales/en';
import enExtra from '@angular/common/locales/extra/en';
import csExtra from '@angular/common/locales/extra/cs';
import deExtra from '@angular/common/locales/extra/de';
import frExtra from '@angular/common/locales/extra/fr';
import viExtra from '@angular/common/locales/extra/vi';

import { AppRoutingModule } from './routing/app-routing.module';
import { AppComponent } from './app.component';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  AbstractI18nService,
  AbstractStateService,
  AbstractStorageService,
  ErrorInterceptor,
} from '@libs/index';
import { LOCALIZATION_LOCALE } from '@libs/index';
import { I18nService } from './services/i18n/i18n.service';
import { PrivatePagesModule } from './pages/private-pages/private-pages.module';
import { PublicPagesModule } from './pages/public-pages/public-pages.module';
import { AuthProcessPagesModule } from './pages/auth-process-pages/auth-process-pages.module';
import { TranslateModule } from '@ngx-translate/core';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { EnvUtils } from './commons/utils/env.utils';
import { GoogleApiService } from './services/google-api/google-api.service';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FeedbackProcessPagesModule } from './pages/feedback-process-pages/feedback-process-pages.module';
import { RedirectLinkPagesModule } from './pages/redirect-link-pages/redirect-link-pages.module';
import { ServiceWorkerModule } from '@angular/service-worker';
import { PwaService } from '@libs/index';
import { CommonsModule } from './commons/commons.module';
import { IframeService } from './services/iframe/iframe.service';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { ServerCallerService } from '@libs/index';
import { AbstractAuthenticationService } from '@libs/index';
import { AuthenticationService } from './services/authentication/authentication.service';
import { StateService } from './services/state/state.service';
import { StorageService } from './services/storage/storage.service';
import { ReferrerTrackingService } from './services/referrer-tracking/referrer-tracking.service';

function initServicesFactory(
  i18nService: AbstractI18nService,
  storageService: AbstractStorageService,
  googleApiService: GoogleApiService,
  pwaService: PwaService,
  iframeService: IframeService,
  serverCallerService: ServerCallerService,
  referrerTrackingService: ReferrerTrackingService
) {
  return async () => {
    await Promise.all([
      i18nService.init(),
      storageService.init(EnvUtils.getEncryptionPassphrase()),
      googleApiService.init(),
      pwaService.init(),
      iframeService.init(),
      serverCallerService.init(EnvUtils.getServerDomain()),
    ]);

    referrerTrackingService.init();
  };
}

registerLocaleData(cs, LOCALIZATION_LOCALE.CZ, csExtra);
registerLocaleData(de, LOCALIZATION_LOCALE.DE, deExtra);
registerLocaleData(fr, LOCALIZATION_LOCALE.FR, frExtra);
registerLocaleData(vi, LOCALIZATION_LOCALE.VN, viExtra);
registerLocaleData(en, LOCALIZATION_LOCALE.US, enExtra);

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    LoggerModule.forRoot({
      level: EnvUtils.isProduction()
        ? NgxLoggerLevel.INFO
        : NgxLoggerLevel.DEBUG,
    }),
    GoogleTagManagerModule.forRoot({
      id: EnvUtils.getGoogleTagManagerId(),
    }),
    TranslateModule.forRoot(),
    BrowserModule,
    CommonsModule,
    BrowserAnimationsModule,
    AuthProcessPagesModule,
    FeedbackProcessPagesModule,
    PrivatePagesModule,
    PublicPagesModule,
    RedirectLinkPagesModule,
    AppRoutingModule,
    ServiceWorkerModule.register('custom-ngsw-worker.js', {
      enabled: EnvUtils.isEnableServiceWorker(),
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
  providers: [
    { provide: AbstractAuthenticationService, useClass: AuthenticationService },
    { provide: AbstractStateService, useClass: StateService },
    { provide: AbstractStorageService, useClass: StorageService },
    { provide: AbstractI18nService, useClass: I18nService },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: APP_INITIALIZER,
      useFactory: initServicesFactory,
      deps: [
        AbstractI18nService,
        AbstractStorageService,
        GoogleApiService,
        PwaService,
        IframeService,
        ServerCallerService,
        ReferrerTrackingService,
      ],
      multi: true,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'fill' },
    },
  ],
})
export class AppModule {}
